import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  CircularProgress,
  Tooltip,
  Box,
  Card,
  CardContent,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Autocomplete,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import {
  Close as CloseIcon,
  AutoFixHigh as AutoFixHighIcon,
  Edit as EditIcon,
  Public as PublicIcon,
  Settings as SettingsIcon,
  PlaylistAdd as PlaylistAddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  AccountTree as AccountTreeIcon,
} from "@material-ui/icons";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { tiposApi } from "src/services/tiposApi";
import { subtareasApi } from "src/services/subtareasApi";
import { sucursalesApi } from "src/services/sucursalesApi";
import { empleadosApi } from "src/services/empleadosApi";
import { tareasApi } from "src/services/tareasApi";
import { AlertaModalV2 } from "../AlertaSwall";
import { useSnackbar } from "src/components/snackbar";
import { CATEGORIAS } from "src/constants";
import { BRAND } from "src/theme/brandTokens";
// ─── Constantes ──────────────────────────────────────────────────────────────

// Array vacío estable: evita que el default `= []` inline en RTK Query cree
// una referencia nueva en cada render, lo que causaría bucles infinitos en
// useEffect cuando estos valores están en el array de dependencias.
const EMPTY_ARRAY = [];

// ─── Estilos ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    backgroundColor: BRAND.color.primary,
    color: "#fff",
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  closeButton: {
    color: "#fff",
    padding: theme.spacing(0.5),
  },
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  sectionCard: {
    marginBottom: theme.spacing(2.5),
    borderRadius: theme.spacing(1),
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${BRAND.color.primary}`,
  },
  sectionIcon: {
    color: BRAND.color.primary,
    fontSize: "1.1rem",
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  readOnlyChip: {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  aiCard: {
    background: "linear-gradient(135deg, #fff8ef 0%, #fff3e0 100%)",
    border: `1px solid ${BRAND.color.primary}30`,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    boxShadow: "0 1px 4px rgba(255,149,0,0.1)",
  },
  aiHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  aiTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  aiHint: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginTop: 2,
  },
  aiButton: {
    background: `linear-gradient(135deg, ${BRAND.color.primary} 0%, ${BRAND.color.primaryDark} 100%)`,
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.75, 2),
    "&:hover": {
      background: `linear-gradient(135deg, ${BRAND.color.primary} 0%, ${BRAND.color.primaryDark} 100%)`,
    },
    "&:disabled": {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  activeChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  inactiveChip: {
    backgroundColor: "#fafafa",
    color: "#9e9e9e",
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  statusBox: {
    display: "flex",
    alignItems: "center",
    padding: "6px 12px",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    height: 40,
    cursor: "pointer",
  },
  subtareaRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  subtareaEmpty: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    fontStyle: "italic",
    paddingLeft: theme.spacing(4),
  },
  sucursalHint: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginTop: theme.spacing(1),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(1),
  },
  cancelButton: {
    textTransform: "none",
    fontWeight: 500,
  },
  saveButton: {
    textTransform: "none",
    fontWeight: 600,
    minWidth: 130,
  },
}));

// ─── Validación ───────────────────────────────────────────────────────────────

const validationSchema = Yup.object().shape({
  valorTarea: Yup.number()
    .typeError("Debe ser un número")
    .min(0, "El valor no puede ser negativo")
    .required("Valor requerido"),
  tipoMoneda: Yup.string().required("Selecciona el tipo de moneda"),
  descripcion: Yup.string().required("Descripción interna requerida"),
});

// ─── Subcomponente: encabezado de sección ─────────────────────────────────────

function SectionHeader({ icon: Icon, title, classes }) {
  return (
    <Box className={classes.sectionHeader}>
      <Icon className={classes.sectionIcon} />
      <Typography variant="subtitle2" className={classes.sectionTitle}>
        {title}
      </Typography>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function EditarTarea({
  tarea,
  openEditar,
  setOpenEditar,
  monedas,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [generando, setGenerando] = useState(false);
  const [subtareaExpandida, setSubtareaExpandida] = useState(null);
  const [nuevaSubtarea, setNuevaSubtarea] = useState("");
  const [sucursalesSeleccionadas, setSucursalesSeleccionadas] =
    useState(EMPTY_ARRAY);

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data: subtareasAsociadas = EMPTY_ARRAY, refetch: refetchSubtareas } =
    tareasApi.useGetTipoTareaQuery(
      { id: tarea?.nombre, tipoTarea: tarea?.tipoTarea },
      { skip: !tarea?.nombre },
    );
  const { data: todasSucursales = EMPTY_ARRAY } =
    sucursalesApi.useGetSucursalesQuery();
  const { data: tareaSucursales = EMPTY_ARRAY } =
    tiposApi.useGetTipoTareaSucursalQuery(tarea?.nombre, {
      skip: !tarea?.nombre,
    });

  // ── Mutations ─────────────────────────────────────────────────────────────
  const [actualizarTarea] = tiposApi.useUpdateTipoTareasMutation();
  const [setSucursalesTarea] = tiposApi.useSetTipoTareaSucursalMutation();
  const [agregarSubtarea] = tiposApi.useAddRelacionTipoTareaSubTareaMutation();
  const [eliminarSubtarea] = subtareasApi.useDeleteTipoTareaSubtareaMutation();
  const [generarDescripcion] = tiposApi.useGenerarDescripcionServicioMutation();

  // Empleados de la sucursal por defecto, para encargado/supervisor.
  const [triggerEmpleados, { data: empleados = EMPTY_ARRAY }] =
    empleadosApi.useLazyGetEmpleadosActivosQuery();

  // ── Inicializar sucursales seleccionadas al abrir ─────────────────────────
  useEffect(() => {
    if (openEditar && tareaSucursales.length > 0) {
      setSucursalesSeleccionadas(tareaSucursales.map((s) => s.ref_sucursal));
    } else if (openEditar) {
      setSucursalesSeleccionadas(EMPTY_ARRAY);
    }
  }, [openEditar, tareaSucursales]);

  // ── Formik ────────────────────────────────────────────────────────────────
  // useMemo estabiliza la referencia: sin esto, enableReinitialize detectaría
  // un objeto nuevo en cada render y llamaría resetForm() → bucle infinito.
  const initialValues = useMemo(
    () => ({
      valorTarea: tarea?.valor ?? 0,
      tipoMoneda: tarea?.tipoMoneda ?? "",
      descripcion: tarea?.descripcion ?? "",
      categoria: tarea?.categoria ?? "",
      descripcionPublica: tarea?.descripcion_publica ?? "",
      objetivo: tarea?.objetivo ?? "",
      alcance: tarea?.alcance ?? "",
      entregable: tarea?.entregable ?? "",
      duracionEstimada: tarea?.duracion_estimada ?? "",
      sucursalDefault: tarea?.sucursal_default ?? "",
      empleadoEncargadoDefault: tarea?.empleado_encargado_default ?? "",
      supervisorDefault: tarea?.supervisor_default ?? "",
      bloquearAsignacion:
        tarea?.bloquear_asignacion !== undefined
          ? Boolean(tarea.bloquear_asignacion)
          : false,
      activo: tarea?.activo !== undefined ? Boolean(tarea.activo) : true,
      orden: tarea?.orden ?? 0,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tarea?.nombre, tarea?.tipoTarea],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const result = await AlertaModalV2({
        title: "¿Guardar cambios?",
        text: `Se actualizará la tarea "${tarea.nombre}".`,
        confirmButtonText: "Sí, guardar",
        icon: "question",
      }).fire();

      if (!result.isConfirmed) return;

      let valorFinal = values.valorTarea;
      if (String(valorFinal).includes(",")) {
        valorFinal = parseFloat(String(valorFinal).replaceAll(",", "."));
      }

      try {
        await actualizarTarea({
          nombre: tarea.nombre,
          tipoTarea: tarea.tipoTarea,
          valor: valorFinal,
          tipoMoneda: values.tipoMoneda,
          descripcion: values.descripcion,
          categoria: values.categoria || undefined,
          descripcionPublica: values.descripcionPublica || undefined,
          objetivo: values.objetivo || undefined,
          alcance: values.alcance || undefined,
          entregable: values.entregable || undefined,
          duracionEstimada: values.duracionEstimada || undefined,
          sucursalDefault: values.sucursalDefault || undefined,
          empleadoEncargadoDefault:
            values.empleadoEncargadoDefault || undefined,
          supervisorDefault: values.supervisorDefault || undefined,
          bloquearAsignacion: values.bloquearAsignacion ? 1 : 0,
          activo: values.activo ? 1 : 0,
          orden: values.orden,
        }).unwrap();

        await setSucursalesTarea({
          nombre: tarea.nombre,
          sucursales: sucursalesSeleccionadas,
        }).unwrap();

        enqueueSnackbar("Tarea actualizada correctamente", {
          variant: "success",
        });
        setOpenEditar(false);
      } catch {
        enqueueSnackbar("Error al actualizar la tarea. Intenta nuevamente.", {
          variant: "error",
        });
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    isSubmitting,
  } = formik;

  // Carga los empleados de la sucursal por defecto (precargada o elegida).
  useEffect(() => {
    if (values.sucursalDefault) {
      triggerEmpleados({ id: values.sucursalDefault, estado: "ALL" });
    }
  }, [values.sucursalDefault, triggerEmpleados]);

  const empleadoLabel = (emp) =>
    emp ? `${emp.nombre ?? ""} ${emp.apellidos ?? ""}`.trim() : "";

  const empleadoEncargadoSel =
    empleados.find((e) => e.rut === values.empleadoEncargadoDefault) ?? null;
  const supervisorSel =
    empleados.find((e) => e.rut === values.supervisorDefault) ?? null;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCerrar = () => {
    setSubtareaExpandida(null);
    setNuevaSubtarea("");
    setOpenEditar(false);
  };

  const handleGenerarConIA = async () => {
    setGenerando(true);
    try {
      const resultado = await generarDescripcion({
        nombre: tarea.nombre,
        descripcionInterna: values.descripcion || undefined,
        tipoServicio: "TAREA",
      }).unwrap();
      setFieldValue("descripcionPublica", resultado.descripcionPublica);
      setFieldValue("objetivo", resultado.objetivo);
      setFieldValue("alcance", resultado.alcance);
      setFieldValue("entregable", resultado.entregable);
      setFieldValue("duracionEstimada", resultado.duracionEstimada);
      enqueueSnackbar("Descripción generada con IA", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al generar la descripción con IA", {
        variant: "error",
      });
    } finally {
      setGenerando(false);
    }
  };

  const handleAgregarSubtarea = async (subtarea) => {
    if (!nuevaSubtarea.trim()) return;
    const yaExiste = subtareasAsociadas.some(
      (s) => s.nombre.toUpperCase() === nuevaSubtarea.toUpperCase(),
    );
    if (yaExiste) {
      enqueueSnackbar("Esa subtarea ya está asociada a esta tarea", {
        variant: "warning",
      });
      return;
    }
    try {
      await agregarSubtarea({
        nombreTarea: tarea.nombre.toUpperCase(),
        tipoTarea: tarea.tipoTarea.toUpperCase(),
        nombre: nuevaSubtarea.toUpperCase(),
      }).unwrap();
      setNuevaSubtarea("");
      refetchSubtareas();
    } catch {
      enqueueSnackbar("Error al agregar la subtarea", { variant: "error" });
    }
  };

  const handleEliminarSubtarea = async (subtarea) => {
    const result = await AlertaModalV2({
      title: "¿Eliminar subtarea?",
      text: `Se eliminará "${subtarea.nombre}" de esta tarea.`,
      confirmButtonText: "Sí, eliminar",
      confirmButtonColor: "#d32f2f",
      icon: "warning",
    }).fire();
    if (!result.isConfirmed) return;
    try {
      await eliminarSubtarea({
        nombre: subtarea.nombre,
        refTipoTarea: subtarea.refTipoTarea,
        tipoTarea: subtarea.tipoTarea,
      }).unwrap();
      refetchSubtareas();
    } catch {
      enqueueSnackbar("Error al eliminar la subtarea", { variant: "error" });
    }
  };

  return (
    // disableEnforceFocus: evita el conflicto de aria-hidden entre MUI Dialog y SweetAlert2.
    <Dialog
      open={openEditar}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      aria-labelledby="editar-tarea-title"
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <DialogTitle className={classes.dialogTitle} id="editar-tarea-title">
        <Box className={classes.titleContent}>
          <EditIcon />
          <Box>
            <Typography
              variant="h6"
              style={{ fontWeight: 700, lineHeight: 1.2 }}
            >
              {tarea?.nombre ?? "Editar Tarea"}
            </Typography>
            <Typography variant="caption" style={{ opacity: 0.85 }}>
              {tarea?.tipoTarea}
            </Typography>
          </Box>
        </Box>
        <IconButton
          className={classes.closeButton}
          onClick={handleCerrar}
          size="small"
          aria-label="Cerrar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogContent className={classes.dialogContent}>
            {/* ── Sección 1: Datos de la tarea ──────────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={EditIcon}
                  title="Datos de la tarea"
                  classes={classes}
                />
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Nombre"
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={tarea?.nombre ?? ""}
                      disabled
                      helperText="El nombre no se puede modificar"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Tipo de tarea"
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={tarea?.tipoTarea ?? ""}
                      disabled
                      helperText="El tipo no se puede modificar"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Valor *"
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0 }}
                      {...getFieldProps("valorTarea")}
                      error={Boolean(touched.valorTarea && errors.valorTarea)}
                      helperText={touched.valorTarea && errors.valorTarea}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={Boolean(touched.tipoMoneda && errors.tipoMoneda)}
                    >
                      <InputLabel>Moneda *</InputLabel>
                      <Select {...getFieldProps("tipoMoneda")} label="Moneda *">
                        <MenuItem value="">
                          <em>Seleccionar</em>
                        </MenuItem>
                        {monedas.map((m, i) => (
                          <MenuItem key={i} value={m.nombre}>
                            {m.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.tipoMoneda && errors.tipoMoneda && (
                        <Typography
                          variant="caption"
                          color="error"
                          style={{ marginLeft: 14, marginTop: 3 }}
                        >
                          {errors.tipoMoneda}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>Categoría</InputLabel>
                      <Select {...getFieldProps("categoria")} label="Categoría">
                        <MenuItem value="">
                          <em>Sin categoría</em>
                        </MenuItem>
                        {CATEGORIAS.map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Descripción interna *"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      {...getFieldProps("descripcion")}
                      error={Boolean(touched.descripcion && errors.descripcion)}
                      helperText={
                        (touched.descripcion && errors.descripcion) ||
                        "Visible solo para el equipo interno"
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 2: Información pública + IA ──────────────────────── */}
            <Card className={classes.aiCard}>
              <CardContent>
                <Box className={classes.aiHeader}>
                  <Box className={classes.aiTitleGroup}>
                    <PublicIcon className={classes.sectionIcon} />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        className={classes.sectionTitle}
                      >
                        Información pública del servicio
                      </Typography>
                      <Typography className={classes.aiHint}>
                        Visible para el cliente en propuestas comerciales
                      </Typography>
                    </Box>
                  </Box>

                  <Tooltip
                    title="Genera objetivo, alcance, entregable y duración automáticamente con IA"
                    placement="left"
                  >
                    <span>
                      <Button
                        className={classes.aiButton}
                        onClick={handleGenerarConIA}
                        disabled={generando}
                        startIcon={
                          generando ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <AutoFixHighIcon />
                          )
                        }
                        size="small"
                      >
                        {generando ? "Generando..." : "Generar con IA"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Descripción pública"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="Describe el servicio en términos que el cliente pueda entender..."
                      helperText={`${(values.descripcionPublica || "").length} caracteres`}
                      {...getFieldProps("descripcionPublica")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Objetivo"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="¿Cuál es el propósito de esta tarea?"
                      {...getFieldProps("objetivo")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Alcance"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="¿Qué incluye esta tarea?"
                      {...getFieldProps("alcance")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Entregable"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="¿Qué se entrega al completar esta tarea?"
                      {...getFieldProps("entregable")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Duración estimada"
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Ej: 3-5 días hábiles"
                      helperText="Tiempo aproximado para completar la tarea"
                      {...getFieldProps("duracionEstimada")}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 3: Configuración ──────────────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={SettingsIcon}
                  title="Configuración"
                  classes={classes}
                />
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Orden de visualización"
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0 }}
                      helperText="Menor número = aparece primero"
                      {...getFieldProps("orden")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box className={classes.statusBox}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.activo}
                            onChange={(e) =>
                              setFieldValue("activo", e.target.checked)
                            }
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Chip
                            label={values.activo ? "Activo" : "Inactivo"}
                            size="small"
                            className={
                              values.activo
                                ? classes.activeChip
                                : classes.inactiveChip
                            }
                          />
                        }
                        style={{ margin: 0 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 3b: Derivación por defecto ────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={AccountTreeIcon}
                  title="Derivación por defecto (opcional)"
                  classes={classes}
                />
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={todasSucursales}
                      getOptionLabel={(option) => option.nombre ?? option}
                      isOptionEqualToValue={(option, value) =>
                        (option.nombre ?? option) === (value.nombre ?? value)
                      }
                      value={
                        todasSucursales.find(
                          (s) => s.nombre === values.sucursalDefault,
                        ) ?? null
                      }
                      onChange={(_, newValue) => {
                        setFieldValue(
                          "sucursalDefault",
                          newValue ? newValue.nombre : "",
                        );
                        // Reinicia encargado/supervisor al cambiar de sucursal.
                        setFieldValue("empleadoEncargadoDefault", "");
                        setFieldValue("supervisorDefault", "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Sucursal por defecto"
                          placeholder="Seleccionar sucursal..."
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={empleados}
                      disabled={!values.sucursalDefault}
                      getOptionLabel={(option) => empleadoLabel(option)}
                      isOptionEqualToValue={(option, value) =>
                        option.rut === value.rut
                      }
                      value={empleadoEncargadoSel}
                      onChange={(_, newValue) =>
                        setFieldValue(
                          "empleadoEncargadoDefault",
                          newValue ? newValue.rut : "",
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Encargado por defecto"
                          placeholder={
                            values.sucursalDefault
                              ? "Seleccionar encargado..."
                              : "Selecciona primero una sucursal"
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={empleados}
                      disabled={!values.sucursalDefault}
                      getOptionLabel={(option) => empleadoLabel(option)}
                      isOptionEqualToValue={(option, value) =>
                        option.rut === value.rut
                      }
                      value={supervisorSel}
                      onChange={(_, newValue) =>
                        setFieldValue(
                          "supervisorDefault",
                          newValue ? newValue.rut : "",
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Supervisor por defecto"
                          placeholder={
                            values.sucursalDefault
                              ? "Seleccionar supervisor..."
                              : "Selecciona primero una sucursal"
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.bloquearAsignacion}
                          onChange={(e) =>
                            setFieldValue(
                              "bloquearAsignacion",
                              e.target.checked,
                            )
                          }
                          color="primary"
                          size="small"
                        />
                      }
                      label="Bloquear asignación en la propuesta"
                      style={{ margin: 0 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 4: Subtareas ──────────────────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={PlaylistAddIcon}
                  title="Subtareas asociadas"
                  classes={classes}
                />

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nombre</TableCell>
                        <TableCell width={48} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subtareasAsociadas.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography className={classes.subtareaEmpty}>
                              Sin subtareas asociadas
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {subtareasAsociadas.map((sub) => (
                        <React.Fragment key={sub.nombre}>
                          <TableRow
                            className={classes.subtareaRow}
                            onClick={() =>
                              setSubtareaExpandida(
                                subtareaExpandida === sub.nombre
                                  ? null
                                  : sub.nombre,
                              )
                            }
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                {subtareaExpandida === sub.nombre ? (
                                  <ExpandLessIcon
                                    fontSize="small"
                                    style={{ color: BRAND.color.primary }}
                                  />
                                ) : (
                                  <ExpandMoreIcon
                                    fontSize="small"
                                    style={{ color: "#bbb" }}
                                  />
                                )}
                                {sub.nombre}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEliminarSubtarea(sub);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          <TableRow key={`${sub.nombre}-collapse`}>
                            <TableCell
                              colSpan={2}
                              style={{ padding: 0, border: 0 }}
                            >
                              <Collapse
                                in={subtareaExpandida === sub.nombre}
                                unmountOnExit
                              >
                                <Box px={4} py={1.5} bgcolor="#fafafa">
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    {sub.descripcion ||
                                      "Sin descripción registrada"}
                                  </Typography>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Agregar nueva subtarea inline */}
                <Box display="flex" alignItems="center" gap={1} mt={2}>
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Nueva subtarea"
                    value={nuevaSubtarea}
                    onChange={(e) => setNuevaSubtarea(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAgregarSubtarea();
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAgregarSubtarea}
                    disabled={!nuevaSubtarea.trim()}
                    style={{ textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    Agregar
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* ── Sección 5: Sucursales ─────────────────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={BusinessIcon}
                  title="Disponibilidad por sucursal"
                  classes={classes}
                />
                <Autocomplete
                  multiple
                  options={todasSucursales.map((s) => s.nombre)}
                  value={sucursalesSeleccionadas}
                  onChange={(_, newValue) =>
                    setSucursalesSeleccionadas(newValue)
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option}
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label="Sucursales habilitadas"
                    />
                  )}
                />
                <Typography className={classes.sucursalHint}>
                  Si no seleccionas ninguna sucursal, la tarea estará disponible
                  en todas.
                </Typography>
              </CardContent>
            </Card>
          </DialogContent>

          {/* ── Footer ──────────────────────────────────────────────────────── */}
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={handleCerrar}
              variant="outlined"
              className={classes.cancelButton}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.saveButton}
              disabled={isSubmitting}
              startIcon={
                isSubmitting && <CircularProgress size={16} color="inherit" />
              }
            >
              {isSubmitting ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
}
